import useFeatureFlags from "hooks-lib/LaunchDarkly/useFeatureFlags"
import { SpeedInsights } from "@vercel/speed-insights/next"
import { useEffect, useState, useCallback } from "react"
import { isBot } from "utils-lib/analytics/isBot"

const SPEED_INSIGHTS_FLAGS = ["LDFFConfigureSpeedInsightsSamplingRate"]

type SpeedInsightsFlags = {
  LDFFConfigureSpeedInsightsSamplingRate: number
  isLDClientReady: boolean
}

const CustomSpeedInsights = () => {
  const [hasSpeedInsightsInitialized, setHasSpeedInsightsInitialized] =
    useState(false)
  const [disableSpeedInsights, setDisableSpeedInsights] = useState(false)
  const [samplingRate, setSamplingRate] = useState(1) // default speed insights with 100% sampling rate

  const { isLDClientReady, LDFFConfigureSpeedInsightsSamplingRate } =
    useFeatureFlags<SpeedInsightsFlags>(SPEED_INSIGHTS_FLAGS)

  const disableSpeedInsightsForBots = useCallback(async () => {
    await isBot()

    let isBotDetected = false

    // Check if the cookie to disable speed insights exists
    // OnCrawl is triggering page insights and using up our utilization, so we are disabling speed insights for bots and crawlers
    if (window.document.cookie?.includes("disable_speed_insights=true")) {
      // If the cookie is set, we are disabling speed insights
      // This is set by the middleware when we detect a bot or crawler
      // We are using a cookie to communicate with the middleware because these are static pages
      // We can aslo either rate limit or disable speed insights based on the sampling rate in launch darkly or by disabling in the vercel dashboard
      isBotDetected = true
      // Remove the cookie after using it (we're using a cookie to communicate with the middleware because these are static pages)
      document.cookie = "disable_speed_insights=; path=/; max-age=0"
    }

    try {
      const botdResult = sessionStorage.getItem("botdResult")
      const parsedBotResults =
        botdResult && typeof botdResult === "string"
          ? (JSON.parse(botdResult) as { bot?: boolean })
          : {}
      // check is the botdResult is set and if the bot is detected, we are disabling speed insights if the cookie is not set in the middleware
      // this is an extra redundancy to make sure we are not enabling speed insights for bots
      if (!isBotDetected && parsedBotResults?.bot) {
        isBotDetected = parsedBotResults?.bot
      }
    } catch (error) {
      console.error("Error parsing bot detection result:", error)
    }

    // disable speed insights if the bot is detected either by the cookie set in the middleware or by the botdResult
    setDisableSpeedInsights(isBotDetected)
  }, [])
  // If the user agent is not a bot, we are enabling speed insights

  const initializeSpeedInsightsAsync = useCallback(async () => {
    await disableSpeedInsightsForBots()
    // Update sampling rate only if it differs from the current value (default is 100)
    if (samplingRate !== LDFFConfigureSpeedInsightsSamplingRate)
      setSamplingRate(LDFFConfigureSpeedInsightsSamplingRate)

    if (!hasSpeedInsightsInitialized) setHasSpeedInsightsInitialized(true)
  }, [
    disableSpeedInsightsForBots,
    samplingRate,
    LDFFConfigureSpeedInsightsSamplingRate,
    hasSpeedInsightsInitialized,
  ])

  useEffect(() => {
    // Initialize speed insights config only once, it doesn't need to be updated after the first render
    // for this flag, we need to wait until the values are properly defined (client is ready)
    if (isLDClientReady && !hasSpeedInsightsInitialized) {
      initializeSpeedInsightsAsync().catch((error) => {
        console.error("Error initializing speed insights async:", error)
      })
    }
  }, [
    isLDClientReady,
    LDFFConfigureSpeedInsightsSamplingRate,
    hasSpeedInsightsInitialized,
    samplingRate,
    initializeSpeedInsightsAsync,
  ])

  // Render nothing if speed insights are not initialized
  if (!hasSpeedInsightsInitialized) return null

  // Render speed insights with sampling rate if disableSpeedInsights is false (not a bot)
  return !disableSpeedInsights ? (
    <div
      id="vercel-speed-insights-with-sampling-rate"
      data-sampling-rate={samplingRate}
    >
      <SpeedInsights sampleRate={samplingRate} />
    </div>
  ) : null
}

export default CustomSpeedInsights
